import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createTheme, ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { makeStyles } from 'tss-react/mui';
import { IconButton } from "@mui/material";
import { MoreVert, Replay, Search, Star, StarBorder, RateReview } from "@mui/icons-material";
import { Modal, Backdrop, Fade, TextField } from '@mui/material';
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import StarModal from "../StarModal";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import { Tooltip } from "@mui/material";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LanguageIcon from '@mui/icons-material/Language';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import HubIcon from '@mui/icons-material/Hub';
import ScienceIcon from '@mui/icons-material/Science';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CambiarDestinoModal from "../CambiarDestinoModal";
import ReplayIcon from '@mui/icons-material/Replay';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import CloseIcon from '@mui/icons-material/Close';


const useStyles = makeStyles()(theme => ({
	actionButtons: {
		display: "flex",
		alignItems: "center",
		marginRight: 6,
		flex: "none",
		alignSelf: "center",
		marginLeft: "auto",
		"& > *": {
			margin: theme.spacing(0.5),
		},
	},
	select: {
		width: 180,
		"&.MuiFormControl-root": {
			marginTop: 0,
			marginBottom: 0,
		},
		"& .MuiInputBase-root": {
			height: 36,
			fontSize: 13,
		},
		"& .MuiOutlinedInput-notchedOutline": {
			borderColor: theme.palette.mode === "light" ? undefined : "rgba(255,255,255,0.14)",
		},
	},
	button: {
		margin: theme.spacing(1),
	},
	modal: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: 16,
	},
	paper: {
		position: 'relative',
		backgroundColor: theme.palette.background.paper,
		boxShadow:
			theme.palette.mode === 'light'
				? '0 24px 60px rgba(16,24,40,0.24)'
				: '0 24px 60px rgba(0,0,0,0.65)',
		width: 460,
		maxWidth: '92vw',
		borderRadius: 16,
		overflow: 'hidden',
		outline: 'none',
	},
	modalClose: {
		position: 'absolute',
		top: 10,
		right: 10,
		color: theme.palette.text.secondary,
	},
	modalHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: 14,
		padding: '22px 24px',
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	modalIconCircle: {
		flex: '0 0 auto',
		width: 46,
		height: 46,
		borderRadius: '50%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		'& svg': { fontSize: 26 },
	},
	modalTitle: {
		margin: 0,
		fontSize: 18,
		fontWeight: 700,
		lineHeight: 1.2,
		color: theme.palette.text.primary,
	},
	modalSubtitle: {
		margin: '4px 0 0',
		fontSize: 13,
		lineHeight: 1.35,
		color: theme.palette.text.secondary,
	},
	modalBody: {
		padding: '22px 24px 20px',
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
	},
	formControl: {
		width: '100%',
	},
	buttonGroup: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: 10,
		marginTop: 4,
	},
	cancelBtn: {
		textTransform: 'none',
		fontWeight: 600,
		borderRadius: 10,
		color: theme.palette.text.secondary,
	},
	submitBtn: {
		textTransform: 'none',
		fontWeight: 700,
		borderRadius: 10,
		paddingLeft: 20,
		paddingRight: 20,
		boxShadow: 'none',
	},
}));


const CANALES = {
	whatsapp: { texto: "WhatsApp", color: "#25d366", Icono: WhatsAppIcon },
	whatsapp_cloud: { texto: "WhatsApp Cloud", color: "#25d3c1", Icono: WhatsAppIcon },
	webchat: { texto: "Chat web", color: "#3956ff", Icono: LanguageIcon },
	facebook: { texto: "Messenger", color: "#3b5998", Icono: FacebookIcon },
	instagram: { texto: "Instagram", color: "#e1306c", Icono: InstagramIcon },
	sandbox: { texto: "Sandbox", color: "#8b8f9a", Icono: ScienceIcon },
};

const ConexionActual = ({ ticket, onClick }) => {
	const info = CANALES[ticket?.channel];
	const Icono = info?.Icono || HubIcon;
	const color = info?.color || "#8b8f9a";

	const nombre = ticket?.whatsapp?.name || info?.texto || "Sin conexión";

	return (
		<Tooltip title={`Respondiendo por ${nombre}. Pulsa para cambiar de conexión, departamento o agente.`}>
			<button
				type="button"
				onClick={onClick}
				style={{
					display: "inline-flex",
					alignItems: "center",
					gap: 6,
					height: 36,
					padding: "0 12px",
					borderRadius: 18,
					maxWidth: 220,
					font: "inherit",
					fontSize: 13,
					fontWeight: 600,
					color,
					backgroundColor: `${color}14`,
					border: `1px solid ${color}3d`,
					whiteSpace: "nowrap",
					overflow: "hidden",
					cursor: "pointer",
				}}
			>
				<Icono style={{ fontSize: 17, flexShrink: 0 }} />
				<span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{nombre}</span>
				<SwapHorizIcon style={{ fontSize: 17, flexShrink: 0, opacity: 0.75 }} />
			</button>
		</Tooltip>
	);
};

const TicketActionButtonsCustom = ({ ticket, contact, searchOpen, onToggleSearch, starredOnly, onToggleStarred }) => {
	const { classes } = useStyles();
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);
	const { setCurrentTicket } = useContext(TicketsContext);
	const [activityLog, setActivityLog] = React.useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [reason, setReason] = useState('');
	const [comment, setComment] = useState('');
	const [starModalOpen, setStarModalOpen] = useState(false);

	const [typeInfoContact, setTypeInfoContact] = useState([]);


	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	useEffect(() => {
		api.get(`/settings`).then(({ data }) => {
			if (Array.isArray(data)) {
				const activitylog = data.find((d) => d.key === "activitylog");
				if (activitylog) {
					setActivityLog(activitylog.value === "enabled");
				}

			}
		});

		api.get("/close-reason", { params: { onlyActive: true } })
			.then(({ data }) => {
				setTypeInfoContact(Array.isArray(data) ? data : []);
			})
			.catch((err) => {
				console.error("No se pudieron cargar los motivos de cierre:", err?.message);
			});
	}, []);


	const vieneDeAnuncio = Boolean(ticket?.adReferralSourceId);

	const handleUpdateTicketStatus = async (e, status, userId) => {
		if (status == 'closed' && (activityLog || vieneDeAnuncio)) {
			return openModal()
		}
		handleUpdateTicket(e, status, userId);
	};

	const handleUpdateTicket = async (e, status, userId, motivo, comentario) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				closeReasonId: motivo ? motivo.id : null,
				closeReason: motivo ? motivo.name : null,
				closeComment: comentario || null,
				userId: userId || null,
				useIntegration: status === "closed" ? false : ticket.useIntegration,
				promptId: status === "closed" ? null : ticket.promptId,
				integrationId: status === "closed" ? null : ticket.integrationId
			});

			setLoading(false);
			if (status === "open") {
				setCurrentTicket({ ...ticket, code: "#open" });
			} else {
				setCurrentTicket({ id: null, code: null })
				navigate("/tickets");
			}
			return true;
		} catch (err) {
			setLoading(false);
			toastError(err);
			return false;
		}
	}

	const handleBlockTicket = async () => {
		try {
			await api.post(`/tickets/block/${ticket.id}`);
			setCurrentTicket({ ...ticket, code: "#open" });

		} catch (err) {
			toastError(err);
		}
	};


	const handleSubmit = async (e) => {
		e.preventDefault();

		if (loading) return;

		try {
			const motivo = typeInfoContact.find((o) => String(o.id) === String(reason));

			if (!motivo) {
				toast.warning("Selecciona el tipo de atención antes de finalizar el chat.");
				return;
			}

			const cerrado = await handleUpdateTicket(e, "closed", user?.id, motivo, comment);
			if (cerrado) {
				setReason("");
				setComment("");
				closeModal();
			}
		} catch (err) {
			toastError(err);
		}

	};


	const [destinoAbierto, setDestinoAbierto] = useState(false);

	const openModal = () => setIsOpen(true);
	const closeModal = () => setIsOpen(false);

	return (
        <div className={classes.actionButtons}>
            {}
            <CambiarDestinoModal
                modalOpen={destinoAbierto}
                onClose={() => setDestinoAbierto(false)}
                ticket={ticket}
            />
            {typeof onToggleSearch === "function" && (
                <IconButton onClick={onToggleSearch} color={searchOpen ? "primary" : "default"} size="small">
                    <Search fontSize="small" />
                </IconButton>
            )}
            {typeof onToggleStarred === "function" && (
                <IconButton onClick={onToggleStarred} size="small">
                    {starredOnly ? <Star fontSize="small" style={{ color: "#f5b400" }} /> : <StarBorder fontSize="small" />}
                </IconButton>
            )}
            {(user?.profile === "supervisor" || user?.profile === "admin") && (
                <IconButton onClick={() => setStarModalOpen(true)} size="small" title="Calificar">
                    <RateReview fontSize="small" />
                </IconButton>
            )}
            {!(contact.isActive) ? (
				<Button
					variant="contained"
					color="primary"
					size="small"
					className={classes.button}
					startIcon={<HowToRegIcon />}
					onClick={e => handleBlockTicket()}
				>
					Desbloquear
				</Button>
			) : (
				<>
					{ticket.status === "closed" && (
						<ButtonWithSpinner
							loading={loading}
							startIcon={<Replay />}
							size="small"
							onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
						>
							{i18n.t("messagesList.header.buttons.reopen")}
						</ButtonWithSpinner>
					)}
					{ticket.status === "open" && (
						<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
							{}
							<ConexionActual ticket={ticket} onClick={() => setDestinoAbierto(true)} />
							<Button
								variant="contained"
								color="primary"
								size="small"
								style={{ whiteSpace: 'nowrap', height: 36 }}
								startIcon={<HowToRegIcon />}
								onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
							>
								Finalizar Chat
							</Button>

							<IconButton onClick={handleOpenTicketOptionsMenu} size="large">
								<MoreVert />
							</IconButton>
							<TicketOptionsMenu
								ticket={ticket}
								anchorEl={anchorEl}
								menuOpen={ticketOptionsMenuOpen}
								handleClose={handleCloseTicketOptionsMenu}
							/>
						</div>
					)}
					{ticket.status === "favorite" && (
						<>

							<Button
								variant="contained"
								color="primary"
								size="small"
								className={classes.button}
								startIcon={<HowToRegIcon />}
								onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
							>
								Finalizar Chat
							</Button>
							<Button
								variant="contained"
								color="primary"
								size="small"
								className={classes.button}
								startIcon={<ReplayIcon />}
								onClick={e => handleUpdateTicketStatus(e, "pending", null)}
							>
								Devolver a Pendientes
							</Button>
							<IconButton onClick={handleOpenTicketOptionsMenu} size="large">
								<MoreVert />
							</IconButton>
							<TicketOptionsMenu
								ticket={ticket}
								anchorEl={anchorEl}
								menuOpen={ticketOptionsMenuOpen}
								handleClose={handleCloseTicketOptionsMenu}
							/>
						</>
					)}
					{ticket.status === "pending" && (
						<ButtonWithSpinner
							loading={loading}
							size="small"
							variant="contained"
							color="primary"
							onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
						>
							{i18n.t("messagesList.header.buttons.accept")}
						</ButtonWithSpinner>
					)}
				</>
			)}
            <Modal
				open={isOpen}
				onClose={closeModal}
				className={classes.modal}
				closeAfterTransition
				slots={{
                    backdrop: Backdrop
                }}
				slotProps={{
                    backdrop: {
                        timeout: 500,
                    }
                }}
			>
				<Fade in={isOpen}>
					<div className={classes.paper}>
						<IconButton
							size="small"
							className={classes.modalClose}
							onClick={closeModal}
							aria-label="cerrar"
						>
							<CloseIcon fontSize="small" />
						</IconButton>

						<div className={classes.modalHeader}>
							<div className={classes.modalIconCircle}>
								<FactCheckOutlinedIcon />
							</div>
							<div>
								<h2 className={classes.modalTitle}>Cierre del ticket</h2>
								<p className={classes.modalSubtitle}>
									{vieneDeAnuncio
										? "Este chat llegó por un anuncio: el motivo es obligatorio para medir qué trajo cada campaña."
										: "Registra el motivo de la atención antes de finalizar el chat."}
								</p>
							</div>
						</div>

						<form onSubmit={handleSubmit} className={classes.modalBody}>
							<TextField
								select
								label="Tipo de atención"
								value={reason}
								onChange={(e) => setReason(e.target.value)}
								className={classes.formControl}
								variant="outlined"
								size="small"
								required
							>
								<MenuItem value="">
									<em>Selecciona un tipo</em>
								</MenuItem>
								{typeInfoContact.map((option) => (
									<MenuItem key={option.id} value={option.id}>
										{option.name}
									</MenuItem>
								))}
							</TextField>
							<TextField
								label="Comentario adicional (opcional)"
								multiline
								rows={4}
								value={comment}
								onChange={(e) => setComment(e.target.value)}
								variant="outlined"
								size="small"
								className={classes.formControl}
							/>

							<div className={classes.buttonGroup}>
								<Button
									variant="text"
									onClick={closeModal}
									className={classes.cancelBtn}
								>
									Cancelar
								</Button>
								<Button
									type="submit"
									variant="contained"
									color="primary"
									className={classes.submitBtn}
									disabled={loading || !reason}
								>
									Finalizar chat
								</Button>
							</div>
						</form>
					</div>
				</Fade>
			</Modal>
			<StarModal
				open={starModalOpen}
				onClose={() => setStarModalOpen(false)}
				ticketId={ticket.id}
				companyId={ticket.companyId}
				idUser={user?.id}
			/>
        </div>
    );
};

export default TicketActionButtonsCustom;
